import { AnimatePresence } from "framer-motion";
import { useMemo, useCallback } from "react";

import { IntegrationCodeSnippet } from "@/components/flow/shared/Deploy/IntegrationCodeSnippet";
import { generateIntegrationCMS } from "@/components/flow/shared/Deploy/documentation/generateIntegrationCMS";
import {
    ImageDeployment,
    VideoDeployment,
    DataMode
} from "@/components/flow/shared/Deploy/documentation/types";
import { useDeployContext } from "@/contexts/DeployContext";
import { QRCodeDisplay } from "@/components/flow/shared/QRCode/QRCodeDisplay";
import {
    useCurrentWorkspace,
    useWorkflowToken,
    useTokenLoading,
    useCurrentMedia,
    useMediaStore
} from "@/store";
import FilePreview from "@/components/file/Preview";
import { FileCarousel } from "@/components/file/Carousel/FileCarousel";
import { useMedia } from "@/contexts/MediaContext";
import { useCoreApp } from "@/contexts/CoreAppContext";
import { BoundingBoxStrategyImpl } from "@utils/annotationStrategies/BoundingBox";
import { FilePreviewProvider } from "@/contexts/FilePreviewProvider";
import { useTestWithMoreFiles } from "@/useTestWithMoreFiles";
import { SuccessBanner } from "./SuccessBanner";
import { VideoTrimmer } from "@/components/file/VideoTrimmer";
import { OutOfCreditsModal } from "@/components/flow/shared/OutOfCreditsModal";
import { MediaItemStatus } from "@/types/media";
import { getExtractedFrameIdsForMediaItem } from "@/hooks/useMediaUtils";

const TestFilesVisualAreaContent = () => {
    const { showLabels } = useCoreApp();
    const {
        testWithMoreFiles,
        uploadLimitError,
        clearUploadLimitError,
        isOutOfCredits,
        clearOutOfCreditsError
    } = useTestWithMoreFiles();
    const { mediaItemToTrim, pendingMediaItems, mediaItems } = useMedia();
    const currentMedia = useCurrentMedia();
    const media = useMediaStore();

    const onAddMediaItems = useCallback(
        async (files: File[], onFinally?: () => void) => {
            await testWithMoreFiles(files);

            onFinally?.();
        },
        [testWithMoreFiles]
    );

    // Check if current file is still uploading/processing
    const isCurrentFileProcessing = useMemo(() => {
        if (!currentMedia?.file) return false;

        const currentFile = currentMedia.file;

        // Check if it's in pending items
        if (pendingMediaItems.some((item) => item.id === currentFile.id)) {
            return true;
        }

        // Check if it's uploading or processing
        if (
            currentFile.status === MediaItemStatus.UPLOADING ||
            currentFile.status === MediaItemStatus.PROCESSING
        ) {
            return true;
        }

        // For videos, check if extracted frames are still uploading
        if (currentFile.isVideo) {
            const extractedFrameIds = getExtractedFrameIdsForMediaItem(currentFile);
            const uploadingFrames = extractedFrameIds.some(
                (frameId) => media[frameId]?.status === MediaItemStatus.UPLOADING
            );
            if (uploadingFrames) {
                return true;
            }
        }

        return false;
    }, [currentMedia?.file, pendingMediaItems, media]);

    return (
        <AnimatePresence mode="wait">
            <div className="visualArea relative">
                <div className="flex min-h-0 flex-1">
                    <FilePreviewProvider>
                        <FileCarousel
                            className="h-full w-full"
                            onAddMediaItems={onAddMediaItems}
                            uploadLimitError={uploadLimitError}
                            clearUploadLimitError={clearUploadLimitError}
                        >
                            <FilePreview
                                key={`file-${currentMedia?.file.id}`}
                                forceHideLabels={!showLabels}
                                strategy={BoundingBoxStrategyImpl}
                                showProcessingOverlay={isCurrentFileProcessing}
                                processingOverlayText="Processing file..."
                            />
                        </FileCarousel>
                    </FilePreviewProvider>
                </div>

                {/* Video Trimmer Modal */}
                {mediaItemToTrim?.validation?.needsTrimming === true && <VideoTrimmer />}

                {/* Out of Credits Modal */}
                <OutOfCreditsModal isOpen={isOutOfCredits} onClose={clearOutOfCreditsError} />
            </div>
        </AnimatePresence>
    );
};

export function DeployVisualAreaContent() {
    const { workflowCredentials, dataMode, workflow } = useDeployContext();
    const workflowToken = useWorkflowToken();
    const tokenLoading = useTokenLoading();

    // Generate copy for IMAGE and VIDEO data modes
    const copy = useMemo(() => {
        // Don't generate copy for PHONE or TEST_FILES mode
        if (dataMode === DataMode.PHONE || dataMode === DataMode.TEST_FILES) {
            return null;
        }

        const integrationCMS = generateIntegrationCMS(workflowCredentials);

        // Get the appropriate deployment type based on data mode
        const getDefaultDeploymentType = () => {
            if (dataMode === DataMode.IMAGE) {
                return ImageDeployment.SINGLE;
            } else if (dataMode === DataMode.VIDEO) {
                return VideoDeployment.FILE;
            }
            return ImageDeployment.SINGLE;
        };

        const selectedDeploymentType = getDefaultDeploymentType();
        return integrationCMS[dataMode][
            selectedDeploymentType as keyof (typeof integrationCMS)[typeof dataMode]
        ];
    }, [workflowCredentials, dataMode]);

    // Render based on data mode
    if (dataMode === DataMode.TEST_FILES) return <TestFilesVisualAreaContent />;

    // Show placeholder div for phone data mode
    if (dataMode === DataMode.PHONE) {
        return (
            <AnimatePresence mode="wait">
                <div className="flex items-center justify-center">
                    <div className="neumorphic-shadow h-[600px] w-80 rounded-[40px] border-black/50 bg-gray-100 p-3">
                        <div className="flex h-full w-full flex-col items-center justify-center gap-6 rounded-3xl bg-white">
                            <div className="flex flex-col items-center justify-center gap-2">
                                <h3 className="text-xl font-semibold text-primary">
                                    Scan your QR code
                                </h3>
                                <p className="text-sm font-normal text-tertiary">
                                    Use your model on your phone camera.
                                </p>
                            </div>

                            {/* QR Code Display */}
                            {workflowToken && !tokenLoading && (
                                <QRCodeDisplay
                                    workflowToken={workflowToken}
                                    workflowUrl={workflow?.url}
                                    size={160}
                                    className="max-w-full"
                                />
                            )}

                            {tokenLoading && (
                                <div className="flex items-center gap-2 text-sm text-gray-500">
                                    <i className="far fa-spinner fa-spin" />
                                    <span>Loading QR code...</span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </AnimatePresence>
        );
    }

    return (
        <AnimatePresence mode="wait">
            <div className="visualArea flex h-full min-w-0 flex-1 flex-col gap-6">
                <SuccessBanner />
                <div className="relative h-full min-w-0 flex-1 overflow-hidden">
                    {copy && <IntegrationCodeSnippet copy={copy} blurred={!workflow} />}
                </div>
            </div>
        </AnimatePresence>
    );
}
